/**
 * `safeParseQueryArray` -- the guard for the JSON blob that arrives in the
 * `query` hash/URL param on a search block (see the shadow header comment in
 * `withSearch.jsx`, which is where the bare, unguarded version lived until
 * now).
 *
 * SPLIT OUT OF `withSearch.jsx` DELIBERATELY, same reasoning as
 * `express/sitemapUrls.js` split out of `sitemapBrowse.js`: testability, not
 * tidiness. `withSearch.jsx` pulls in `query-string`, `react-router-dom` and
 * `react-redux` -- none of which are this addon package's own dependencies,
 * only core Volto's (this package's `vitest.config.mjs` aliases the latter
 * two to core's copies for exactly this reason, but does not carry
 * `query-string`, and adding a workspace-wide alias for one function's sake
 * would be a wider change than the bug needs). A plain module with no
 * imports at all needs no alias and no build -- it is resolvable from a
 * vitest run of this package as-is.
 */

/**
 * Returns `[]` for empty input, input that is not valid JSON, and input that
 * parses to something other than an array -- the three shapes that crashed
 * core's bare `JSON.parse(q)?.map(...)`:
 *
 *   - non-JSON text, e.g. `?query=radiation` -> `JSON.parse` throws
 *     `SyntaxError: Unexpected token 'r', "radiation" is not valid JSON`.
 *     Confirmed live in frontend/fe_directives.log and repeated across the
 *     fleet -- the single most common 5xx cause in the 2026-09-13..27 edge
 *     logs (10 hits on `/search` alone, several through OAI-SearchBot).
 *   - JSON that parses but is not an array, e.g. `?query={"a":1}` -> `{}.map`
 *     is `undefined`, so calling it throws `TypeError: ... is not a
 *     function`.
 *   - `null`/other falsy-but-valid JSON, e.g. `?query=null` -> `null?.map` is
 *     `undefined`, which the two withSearch.jsx call sites then treat as an
 *     array (`.find`, spreading into `Object.assign`), crashing one level
 *     further down instead.
 */
export const safeParseQueryArray = (q) => {
  if (!q) return [];
  let parsed;
  try {
    parsed = JSON.parse(q);
  } catch (error) {
    return [];
  }
  return Array.isArray(parsed) ? parsed : [];
};
